/**
 *  "globalSwatches" is all the linked products, that is used to create the swatches. these are saved in the session storage.
 */
let globalSwatches = JSON.parse(sessionStorage.getItem('swatches'));

// referenceRegex is used to split the SKU to get the reference
const referenceRegex = /^([^\\]+)/;

/**
 * The main background color of the page.
 * @type {string}
 */
const mainBackgroundColor = getComputedStyle(document.documentElement).getPropertyValue('--background') ? getComputedStyle(document.documentElement).getPropertyValue('--background') : '#ffffff';

/**
 * Helpers
 */
const groupBy = (data, ...keys) => {
  return data.reduce((results, item) => {
    const group = keys.reduce((obj, key) => obj[key] ?? {}, item);
    results[group] = [...(results[group] || []), item];
    return results;
  }, {});
};

//return if the the user, uses touch, or not
const isTouchDevice = () => 'ontouchstart' in window || navigator.maxTouchPoints > 0;

// https://dev.to/alvaromontoro/building-your-own-color-contrast-checker-4j7o
// Calculate the relative luminance of a color to check if it is dark or light
//this code is used to calculate the contrast ratio between two colors according to the WCAG guidelines. This is important for ensuring that text is readable against its background color.

const hexToRgb = (hex) => {
  const normalizedHex = hex.replace(/^#/, '');
  const isShortHex = normalizedHex.length === 3;
  let r, g, b;

  if (isShortHex) {
    [r, g, b] = Array.from(normalizedHex, (char) => parseInt(char + char, 16));
  } else {
    [r, g, b] = Array.from(normalizedHex.matchAll(/.{2}/g), ([value]) => parseInt(value, 16));
  }

  return { r, g, b }
}

const calculateColorRatio = ({ color1, color2 }) => {

  // read the colors and transform them into rgb format
  const color1rgb = hexToRgb(color1);
  const color2rgb = hexToRgb(color2);

  // calculate the relative luminance
  const color1luminance = luminance(color1rgb.r, color1rgb.g, color1rgb.b);
  const color2luminance = luminance(color2rgb.r, color2rgb.g, color2rgb.b);

  // calculate the color contrast ratio
  const ratio = color1luminance > color2luminance
    ? ((color2luminance + 0.05) / (color1luminance + 0.05))
    : ((color1luminance + 0.05) / (color2luminance + 0.05));

  return ratio;
}

const luminance = (r, g, b) => {
  var a = [r, g, b].map((v) => {
    v /= 255;
    return v <= 0.03928
      ? v / 12.92
      : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
}

const wcagPass = swatchColor => {
  const ratio = calculateColorRatio({ color1: swatchColor, color2: mainBackgroundColor });
  const ratioPass = ratio < (1 / 1.2) ? true : false;
  return ratioPass;

  //   AA-level large text: ${ratio < 1/3 ? 'PASS' : 'FAIL' }<br>
  //   AA-level small text: ${ratio < 1/4.5 ? 'PASS' : 'FAIL' }<br>
  //   AAA-level large text: ${ratio < 1/4.5 ? 'PASS' : 'FAIL' }<br>
  //   AAA-level small text: ${ratio < 1/7 ? 'PASS' : 'FAIL' }
}



const isImageUrl = (url) => {
  url = url.split('?')[0];
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.svg'];
  const extension = url.substring(url.lastIndexOf('.')).toLowerCase();
  return imageExtensions.includes(extension);
};

class GetSwatchesData {
  constructor() {
    this.data = {};
    this.init();
  }

  async init() {
    try {
      await this.getRequestUrls();
      await this.getProducts();
      await this.saveToSesseion();
    } catch (e) {
      console.error(e);
    }

    document.dispatchEvent(new CustomEvent('swatchesDataLoaded'));
  }

  async getRequestUrls() {

    //Build a list of urls to fetch
    return new Promise((resolve) => {
      const requestUrls = [];
      const dataUrl = '/collections/all?view=gtc-swatch-data&page='
      /**Get all available urls */
      if (swatchDataPages > 0) {
        for (let i = 1; i < parseInt(swatchDataPages) + 1; i++) {
          const urlFetch = fetch(
            `${ dataUrl }${ i }`
          ).then((response) => response.json());
          requestUrls.push(urlFetch);
        }
      } else {
        const urlFetch = fetch(
          `${ dataUrl }${ i }`
        ).then((response) => response.json());
        requestUrls.push(urlFetch);
      }
      this.requestUrls = requestUrls;
      resolve();
    });
  }

  /**
   * Retrieves products from multiple request URLs and groups them by reference.
   * @async
   * @returns {Promise} A promise that resolves with the grouped product data.
   */
  async getProducts() {
    try {
      const data = await Promise.all(this.requestUrls);
      const merged = [].concat(...data);
      this.data = groupBy(merged, 'reference');
    } catch (e) {
      console.error(e);
    }
  }

  /**
   * Saves the swatches data to the session storage.
   * @async
   * @function saveToSesseion
   * @returns {Promise<void>}
   */
  async saveToSesseion() {
    window.sessionStorage.setItem('swatches', JSON.stringify(this.data));
    globalSwatches = this.data;
  }
}


document.addEventListener('swatchesDataLoaded', () => {
  class GTCDynamicSwatches extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {

      this.settings = {
        ...JSON.parse(this.getAttribute('settings'))
      };

      if (!this.settings) return

      this.dom = {
        main: this,
        swatchCount: this.querySelector('[data-swatch-count]'),
        swatches: this.querySelector('[data-swatches]'),
        swatchesTemplate: this.querySelector(`[data-template-swatches]`),
        swatchTemplate: this.querySelector(`[data-template-swatch]`),
        gtcTrigger: this.querySelector('gtc-trigger'),
      }

      this.data = {
        swatches: [],
        referenceKey: this.settings.reference.match(referenceRegex)[1],
        swatchTemplate: this.querySelector('[data-swatch]'),
        swatchCount: 0,
        swatchLimit: 0
      }

      this.Init();
    }

    disconnectedCallback() {
    }

    Init() {
      this.GetSwatches();
      this.AddSwatches();
      this.AddSwatchCount();

      if (this.dom.swatchTemplate != null && this.dom.swatchesTemplate != null) {
        this.UpdateSwatchesTemplate();
      }

      this.classList.remove('gtc:visually-hidden');
    }

    GenerateSwatchColorGradient(colorArray) {
      const generateStrictGradient = (colorArray) => {
        const colorStepSize = 100 / colorArray.length;

        const gradientString = colorArray.map((color, index) => {
          const start = index === 0 ? '' : `${ colorStepSize * index }% `;
          const end = index === colorArray.length - 1 ? '' : `${ colorStepSize * (index + 1) }%`;

          return `${ color } ${ start }${ end }`;
        }).join(',');

        return gradientString;
      };

      const gradient = colorArray.length > 1 ? `linear-gradient(135deg, ${ generateStrictGradient(colorArray) })` : colorArray[0];

      return gradient;
    }

    GetSwatches() {
      const swatches = globalSwatches?.[this.data.referenceKey];
      if (swatches) {
        this.data.swatches.push(...swatches);
        this.data.swatchCount = this.data.swatches.length;
      } else {
        console.warn('no globalSwatches');
      }
    }

    CreateSwatch(swatch) {
      const swatchClone = this.data.swatchTemplate.cloneNode(true);
      const swatchContainer = swatchClone.querySelector('.gtc\\:linked-product__container');
      const urlElement = swatchClone.querySelector('[data-url]');

      swatchContainer.removeAttribute('style');
      swatchClone.classList.remove('active');
      swatchClone.classList.add('gtc:fade-in');

      // Check if the linked product has a generic swatch color
      swatch.hasGenericSwatch = false;
      if (swatch.genericColorReference && typeof gtcGenericSwatchConfig !== 'undefined') {
        const genericSwatch = gtcGenericSwatchConfig[swatch.genericColorReference];
        if (genericSwatch && genericSwatch[0]) {
          if (genericSwatch[0].image && isImageUrl(genericSwatch[0].image)) {
            swatch.image = genericSwatch[0].image;
            swatch.hasGenericSwatch = true;
            swatchContainer.innerHTML = '&nbsp;';
          } else if (genericSwatch[0].color) {
            const gradient = this.GenerateSwatchColorGradient(genericSwatch[0].color);
            const wcagPasses = genericSwatch[0].color.map(color => wcagPass(color));

            swatchContainer.style.background = gradient;
            swatch.hasGenericSwatch = true;
            swatch.hasGenericSwatchColor = true;
            swatchContainer.innerHTML = '&nbsp;';

            if (wcagPasses.includes(false)) {
              // This is used to set a darker border around a swatch if the contrast ratio is not high enough
              swatchContainer.setAttribute('data-wcag-pass', 'false');
            }
          }
        }
      }

      // Set color name
      if (swatch.colorName) {
        swatchClone.setAttribute('color-name', swatch.colorName);
      }

      // Set url
      if (urlElement) {
        if (urlElement.getAttribute('href') === swatch.url) {
          swatchClone.classList.add('active');
        }
        urlElement.setAttribute('href', swatch.url);
      }

      // Set image
      if (!swatch.hasGenericSwatchColor && swatch.image) {
        const imageElement = this.GenerateSwatchImage({ swatch, isGenericSwatch: swatch.hasGenericSwatch });
        swatchContainer.replaceChildren(imageElement);
      }

      return swatchClone;
    }

    /**
     * Adds swatches to the swatches container based on the provided settings and data.
     * @function
     * @name AddSwatches
     * @memberof GtcLinkedProductsSku
     * @returns {void}
     */
    AddSwatches() {
      const fragmentBefore = document.createDocumentFragment();
      const fragmentAfter = document.createDocumentFragment();

      const temporarySwatchArray = [...this.data.swatches];
      this.data.swatchLimit = this.settings.limit;

      if (!this.dom.swatchCount && this.data.swatches.length > this.data.swatchLimit) {
        this.data.swatchLimit = this.data.swatchLimit - 1;
      }

      if (this.settings.limit === 0) {
        this.data.swatchLimit = temporarySwatchArray.length;
      }

      const limitCenter = Math.ceil(this.data.swatchLimit / 2);
      const currentProductIndex = temporarySwatchArray.findIndex(swatch => swatch.productId === this.settings.productId);
      let swatchesOffset = 0;

      if (currentProductIndex > limitCenter) {
        swatchesOffset = currentProductIndex - limitCenter;
      }

      const swatchesMaxOffset = temporarySwatchArray.length >= this.data.swatchLimit ? (temporarySwatchArray.length - this.data.swatchLimit) : temporarySwatchArray.length;

      // If the swatchesOffset is bigger than the swatchesMaxOffset, set the swatchesOffset to the swatchesMaxOffset
      if (swatchesOffset > swatchesMaxOffset) {
        swatchesOffset = swatchesMaxOffset;
      }

      // Loop through the swatches and create a swatch for each one, offset by the swatchesOffset and only loop through the swatchLimit
      for (let i = swatchesOffset; i < (swatchesOffset + this.data.swatchLimit); i++) {
        // Break if the index is bigger than the temporarySwatchArray length
        if (i >= temporarySwatchArray.length) {
          break;
        }

        // Skip if the swatch is the current product
        if (this.settings.productId === temporarySwatchArray[i].productId) {
          continue;
        }

        const swatch = this.CreateSwatch(temporarySwatchArray[i]);

        if (swatch) {
          // Add swatchClone to fragment
          if (i < currentProductIndex) {
            fragmentBefore.appendChild(swatch);
          } else {
            fragmentAfter.appendChild(swatch);
          }
        }
      }

      // Add fragment to swatches container
      this.dom.swatches.prepend(fragmentBefore);
      this.dom.swatches.appendChild(fragmentAfter);
    }

    GenerateSwatchImage({ swatch, isGenericSwatch }) {
      const imageElement = document.createElement('img');
      const imageWidth = this.data.swatchTemplate.getAttribute('data-image-width');
      const imageUrl = new URL(swatch.image, window.origin);
      const dataWidths = this.data.swatchTemplate.getAttribute('data-image-widths').split(',');

      imageUrl.searchParams.set('width', imageWidth);
      imageElement.setAttribute('src', imageUrl);
      imageElement.setAttribute('alt', swatch.title);

      const srcset = dataWidths
        .map(width => {
          imageUrl.searchParams.set('width', width);
          return `${ imageUrl } ${ width }w`;
        })
        .join(', ');

      if (isGenericSwatch) {
        imageElement.classList.add('gtc:object-cover');
      }

      imageElement.setAttribute('srcset', srcset);

      return imageElement;
    }


    AddSwatchCount() {
      if (this.dom.swatchCount && this.settings.limit > 0) {
        const swatchCount = this.data.swatchCount - this.settings.limit;

        if (swatchCount > 0) {
          this.dom.swatchCount.innerText = swatchCount;
          this.dom.swatchCount.removeAttribute('hidden');
        }
      }
    }

    UpdateSwatchesTemplate() {
      const swatchesFragment = document.createDocumentFragment();
      const swatchesContainerElement = document.createElement('div');
      swatchesContainerElement.classList.add('gtc:linked-products__container');

      this.data.swatches.forEach(swatchData => {
        const swatch = this.CreateSwatch(swatchData);
        if (swatch) {
          swatchesFragment.append(swatch);
        }
      });

      if (swatchesFragment.hasChildNodes() && swatchesFragment.childNodes.length > 0) {
        swatchesContainerElement.append(swatchesFragment);
        this.dom.swatchesTemplate.content.append(swatchesContainerElement)

      }

      if (this.data.swatches.length > this.data.swatchLimit) {
        this.dom.gtcTrigger.classList.add('gtc:fade-in');
        this.dom.gtcTrigger.removeAttribute('hidden');
      }
      else {
        this.dom.gtcTrigger.remove();
      }
    }

  }
  customElements.define('gtc-dynamic-swatches', GTCDynamicSwatches);

}, { once: true });


/**
 * Checking if storedProductSkusInSession exist, and is not an empty object
 */
if (globalSwatches && Object.keys(globalSwatches).length > 0) {
  document.dispatchEvent(new CustomEvent('swatchesDataLoaded'));
} else {
  void new GetSwatchesData();
}
